import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import {
  REPORT_COLORS,
  bandColors,
  buildManualCommitmentFromTransaction,
  buildManualLumpSumFromTransaction,
  buildInitialReportScenario,
  deriveReportScenario,
  formatMoney,
  formatMultiple,
  formatPercent,
  formatShortMonth,
  getDsrBand,
  getDsrLabel,
  getFccBand,
  getFccLabel,
  getHeatmapColor,
} from "./bankStatementReportUtils";
import { useMediaQuery } from "@mui/material";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const cardStyle = {
  background: REPORT_COLORS.card,
  border: `1px solid ${REPORT_COLORS.line}`,
  borderRadius: "20px",
  padding: "20px",
  boxShadow: "0 18px 48px rgba(11, 31, 58, 0.06)",
  minWidth: 0,
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: "11px",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: REPORT_COLORS.inkSoft,
  fontWeight: 700,
};

const summaryRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
};

const tableHeaderCellStyle = {
  ...labelStyle,
  textAlign: "left",
  padding: "10px 8px",
  borderBottom: `1px solid ${REPORT_COLORS.line}`,
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "12px 8px",
  borderBottom: `1px solid ${REPORT_COLORS.line}`,
  fontSize: "13px",
  color: REPORT_COLORS.ink,
  verticalAlign: "top",
};

const pillStyle = (band) => {
  const colors = bandColors(band);
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 700,
    background: colors.background,
    color: colors.text,
  };
};

const fraudBand = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (["critical", "high"].includes(normalized)) return "red";
  if (["medium", "moderate"].includes(normalized)) return "amber";
  if (["low", "none", "clear"].includes(normalized)) return "green";

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    if (numeric >= 70) return "red";
    if (numeric >= 35) return "amber";
    return "green";
  }

  return "amber";
};

const formatFraudFlagCode = (value) =>
  String(value || "Flag")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatFraudNumber = (value, digits = 4) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(digits);
};

const formatFraudPageSummary = (page) => {
  const pageNumber = page?.page_number ?? page?.page_index;
  const parts = [pageNumber ? `Page ${pageNumber}` : "Page detail"];
  const jaccard = formatFraudNumber(page?.jaccard);

  if (jaccard) parts.push(`Jaccard ${jaccard}`);
  if (page?.pdf_token_count !== undefined) {
    parts.push(`PDF tokens ${page.pdf_token_count}`);
  }
  if (page?.ocr_token_count !== undefined) {
    parts.push(`OCR tokens ${page.ocr_token_count}`);
  }

  return parts.join(" · ");
};

const formatFraudFindingSummary = (finding) => {
  const pageNumber = finding?.page_number ?? finding?.page_index;
  const parts = [
    pageNumber ? `Page ${pageNumber}` : "Finding",
    formatFraudFlagCode(finding?.type || "finding"),
  ];

  if (finding?.text_sample) parts.push(`Sample "${finding.text_sample}"`);
  if (finding?.font && finding?.expected_font) {
    parts.push(`Font ${finding.font} expected ${finding.expected_font}`);
  } else if (finding?.font) {
    parts.push(`Font ${finding.font}`);
  }
  if (finding?.size !== undefined) parts.push(`Size ${finding.size}`);

  return parts.join(" · ");
};

const formatFraudObjectSummary = (value) => {
  if (!value || typeof value !== "object") return String(value ?? "-");
  if ("type" in value || "text_sample" in value) {
    return formatFraudFindingSummary(value);
  }
  if ("page_number" in value || "jaccard" in value) {
    return formatFraudPageSummary(value);
  }

  return Object.entries(value)
    .filter(([, itemValue]) => itemValue !== undefined && itemValue !== null)
    .map(([itemKey, itemValue]) => {
      if (Array.isArray(itemValue)) {
        return `${formatFraudFlagCode(itemKey)} ${itemValue.join(", ")}`;
      }
      if (typeof itemValue === "object") {
        return `${formatFraudFlagCode(itemKey)} ${formatFraudObjectSummary(itemValue)}`;
      }
      return `${formatFraudFlagCode(itemKey)} ${itemValue}`;
    })
    .join(" · ");
};

const formatFraudDetailValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (typeof value === "number") {
    return key.toLowerCase().includes("jaccard")
      ? formatFraudNumber(value)
      : String(value);
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (Array.isArray(value)) {
    if (value.length === 0) return "None";

    if (value.every((item) => item === null || typeof item !== "object")) {
      return value.join(", ");
    }

    return value
      .map((item) => formatFraudObjectSummary(item))
      .filter(Boolean)
      .join("; ");
  }

  if (typeof value === "object") {
    return formatFraudObjectSummary(value);
  }

  return String(value);
};

const getFraudDocumentUrl = (statement) =>
  statement?.document_url ||
  statement?.documentUrl ||
  statement?.file_url ||
  statement?.fileUrl ||
  statement?.public_url ||
  statement?.url ||
  statement?.document?.url ||
  statement?.document?.document_url ||
  "";

const normalizeFraudBbox = (bbox) => {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const values = bbox.slice(0, 4).map((value) => Number(value));
  return values.every(Number.isFinite) ? values : null;
};

const getFraudPageDimension = (finding, dimension, fallback) => {
  const snakeKey = dimension === "width" ? "page_width" : "page_height";
  const camelKey = dimension === "width" ? "pageWidth" : "pageHeight";
  const nestedValue =
    finding?.page && typeof finding.page === "object"
      ? finding.page[dimension] || finding.page[snakeKey] || finding.page[camelKey]
      : null;
  const value =
    finding?.[snakeKey] ||
    finding?.[camelKey] ||
    finding?.[`document_${dimension}`] ||
    nestedValue ||
    fallback;
  const numeric = Number(value);

  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const collectFraudBboxFindings = (source, output = [], seen = new WeakSet()) => {
  if (!source || typeof source !== "object") return output;
  if (seen.has(source)) return output;
  seen.add(source);

  const bbox = normalizeFraudBbox(source.bbox);
  if (bbox) {
    output.push(source);
  }

  Object.values(source).forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((item) => collectFraudBboxFindings(item, output, seen));
    } else if (value && typeof value === "object") {
      collectFraudBboxFindings(value, output, seen);
    }
  });

  return output;
};

const extractFraudBboxFindings = (flag, statement) => {
  const documentUrl = getFraudDocumentUrl(statement);
  const fileName =
    statement?.file_name ||
    statement?.original_file_name ||
    statement?.name ||
    "Bank statement";

  return collectFraudBboxFindings(flag?.details || {})
    .map((finding, index) => {
      const bbox = normalizeFraudBbox(finding?.bbox);
      if (!bbox) return null;

      const pageIndex = Number(finding?.page_index);
      const pageNumberValue = Number(finding?.page_number);
      const pageNumber = Number.isFinite(pageNumberValue)
        ? pageNumberValue
        : Number.isFinite(pageIndex)
          ? pageIndex + 1
          : 1;

      return {
        id: `${flag?.code || "fraud"}-${pageNumber}-${bbox.join("-")}-${index}`,
        bbox,
        pageNumber,
        pageIndex: pageNumber - 1,
        type: finding?.type || flag?.code || "finding",
        label: formatFraudFlagCode(finding?.type || flag?.code || "finding"),
        textSample: finding?.text_sample || "",
        pageWidth: getFraudPageDimension(finding, "width", null),
        pageHeight: getFraudPageDimension(finding, "height", null),
        documentUrl,
        fileName,
      };
    })
    .filter(Boolean);
};

const getRenderedBboxRect = (viewport, finding) => {
  const bbox = finding.bbox;
  const pageWidth = getFraudPageDimension(
    finding,
    "width",
    viewport.width / (viewport.scale || 1),
  );
  const pageHeight = getFraudPageDimension(
    finding,
    "height",
    viewport.height / (viewport.scale || 1),
  );
  const scaleX = viewport.width / pageWidth;
  const scaleY = viewport.height / pageHeight;
  const left = bbox[0] * scaleX;
  const top = bbox[1] * scaleY;
  const width = (bbox[2] - bbox[0]) * scaleX;
  const height = (bbox[3] - bbox[1]) * scaleY;

  return {
    left: width >= 0 ? left : left + width,
    top: height >= 0 ? top : top + height,
    width: Math.abs(width),
    height: Math.abs(height),
  };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatDateLabel = (value) => {
  if (!value) {
    return "-";
  }

  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    return value;
  }
};

const formatCompactMoney = (amount) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return "-";
  }

  if (Math.abs(numeric) >= 1000) {
    return `${(numeric / 1000).toFixed(1)}k`;
  }

  return numeric.toFixed(0);
};

const normalizeHeatmapColumnDays = (days, targetCount = 30) => {
  const sourceDays = Array.isArray(days) ? days.filter(Boolean) : [];

  if (sourceDays.length === 0) {
    return Array.from({ length: targetCount }, (_, index) => ({
      id: `empty-${index}`,
      date: null,
      balance: null,
      isLow: false,
    }));
  }

  const lowSourceIndex = sourceDays.reduce(
    (bestIndex, current, currentIndex) => {
      const currentBalance = Number(current?.balance);
      const bestBalance = Number(sourceDays[bestIndex]?.balance);

      if (!Number.isFinite(bestBalance)) {
        return currentIndex;
      }

      if (!Number.isFinite(currentBalance)) {
        return bestIndex;
      }

      return currentBalance < bestBalance ? currentIndex : bestIndex;
    },
    0,
  );

  const lowTargetIndex =
    sourceDays.length > 1
      ? Math.round(
          (lowSourceIndex / (sourceDays.length - 1)) * (targetCount - 1),
        )
      : 0;

  return Array.from({ length: targetCount }, (_, index) => {
    const sourceIndex =
      targetCount > 1
        ? Math.round((index / (targetCount - 1)) * (sourceDays.length - 1))
        : 0;
    const sourceDay = sourceDays[sourceIndex] || null;

    return {
      id: `${sourceDay?.date || "day"}-${index}`,
      date: sourceDay?.date || null,
      balance: sourceDay?.balance ?? null,
      isLow: index === lowTargetIndex,
    };
  });
};

const getMonthHeatBounds = (month) => {
  const providedMin = Number(month?.min_balance);
  const providedMax = Number(month?.max_balance);

  if (Number.isFinite(providedMin) && Number.isFinite(providedMax)) {
    return { min: providedMin, max: providedMax };
  }

  const balances = (Array.isArray(month?.days) ? month.days : [])
    .map((day) => Number(day?.balance))
    .filter(Number.isFinite);

  if (balances.length === 0) {
    return { min: NaN, max: NaN };
  }

  return {
    min: Math.min(...balances),
    max: Math.max(...balances),
  };
};

const amountRangeOptions = [
  { label: "Any amount", min: "", max: "" },
  { label: "0 - 5,000", min: "0", max: "5000" },
  { label: "5,000 - 10,000", min: "5000", max: "10000" },
  { label: "10,000 - 15,000", min: "10000", max: "15000" },
  { label: "15,000 - 20,000", min: "15000", max: "20000" },
  { label: "20,000 - 25,000", min: "20000", max: "25000" },
  { label: "25,000 - 30,000", min: "25000", max: "30000" },
  { label: "30,000 - 35,000", min: "30000", max: "35000" },
  { label: "35,000 - 40,000", min: "35000", max: "40000" },
  { label: "40,000 - 45,000", min: "40000", max: "45000" },
  { label: "45,000 - 50,000", min: "45000", max: "50000" },
  { label: "50,000+", min: "50000", max: "" },
];

const filterInputStyle = {
  border: `1px solid ${REPORT_COLORS.line}`,
  borderRadius: "8px",
  padding: "9px 10px",
  fontSize: "13px",
  color: REPORT_COLORS.ink,
  minWidth: 0,
  boxSizing: "border-box",
};

const actionButtonStyle = {
  border: `1px solid ${REPORT_COLORS.blue}`,
  background: REPORT_COLORS.blue,
  color: "#fff",
  borderRadius: "8px",
  padding: "9px 12px",
  fontSize: "13px",
  fontWeight: 800,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  ...actionButtonStyle,
  background: "#fff",
  color: REPORT_COLORS.blue,
};

const resetButtonStyle = {
  ...secondaryButtonStyle,
  borderColor: REPORT_COLORS.line,
  color: REPORT_COLORS.inkSoft,
};

const manualEntryGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(180px, 1fr) minmax(140px, 0.45fr) auto",
  gap: "10px",
  alignItems: "center",
  marginTop: "12px",
};

const normalizeSearchText = (value) => String(value || "").trim().toLowerCase();
const TRANSACTION_PICKER_PAGE_SIZE = 40;

const FraudBboxThumbnail = ({ finding }) => {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState(
    finding.documentUrl ? "loading" : "unavailable",
  );

  useEffect(() => {
    if (!finding.documentUrl || !canvasRef.current) {
      setStatus("unavailable");
      return undefined;
    }

    let cancelled = false;
    let loadingTask = null;
    let renderTask = null;
    let pdfDocument = null;

    const renderThumbnail = async () => {
      try {
        setStatus("loading");
        loadingTask = pdfjsLib.getDocument(finding.documentUrl);
        pdfDocument = await loadingTask.promise;
        const page = await pdfDocument.getPage(finding.pageNumber);
        const viewport = page.getViewport({ scale: 1.4 });
        const sourceCanvas = document.createElement("canvas");
        const sourceContext = sourceCanvas.getContext("2d");

        sourceCanvas.width = viewport.width;
        sourceCanvas.height = viewport.height;
        renderTask = page.render({
          canvasContext: sourceContext,
          viewport,
        });
        await renderTask.promise;

        if (cancelled || !canvasRef.current) return;

        const rect = getRenderedBboxRect(viewport, finding);
        const padding = 56;
        const cropLeft = clamp(rect.left - padding, 0, sourceCanvas.width);
        const cropTop = clamp(rect.top - padding, 0, sourceCanvas.height);
        const cropRight = clamp(
          rect.left + rect.width + padding,
          cropLeft + 1,
          sourceCanvas.width,
        );
        const cropBottom = clamp(
          rect.top + rect.height + padding,
          cropTop + 1,
          sourceCanvas.height,
        );
        const cropWidth = cropRight - cropLeft;
        const cropHeight = cropBottom - cropTop;
        const outputCanvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        const outputWidth = 190;
        const outputHeight = 92;
        const outputContext = outputCanvas.getContext("2d");

        outputCanvas.width = outputWidth * dpr;
        outputCanvas.height = outputHeight * dpr;
        outputContext.setTransform(dpr, 0, 0, dpr, 0, 0);
        outputContext.fillStyle = "#fff";
        outputContext.fillRect(0, 0, outputWidth, outputHeight);
        outputContext.drawImage(
          sourceCanvas,
          cropLeft,
          cropTop,
          cropWidth,
          cropHeight,
          0,
          0,
          outputWidth,
          outputHeight,
        );

        const scaleX = outputWidth / cropWidth;
        const scaleY = outputHeight / cropHeight;
        outputContext.strokeStyle = "#dc2626";
        outputContext.lineWidth = 2;
        outputContext.strokeRect(
          (rect.left - cropLeft) * scaleX,
          (rect.top - cropTop) * scaleY,
          Math.max(rect.width * scaleX, 3),
          Math.max(rect.height * scaleY, 3),
        );
        setStatus("ready");
      } catch (error) {
        if (!cancelled) {
          setStatus("error");
        }
      }
    };

    renderThumbnail();

    return () => {
      cancelled = true;
      if (renderTask?.cancel) renderTask.cancel();
      if (loadingTask?.destroy) loadingTask.destroy();
      if (pdfDocument?.destroy) pdfDocument.destroy();
    };
  }, [finding]);

  return (
    <div
      style={{
        width: "190px",
        maxWidth: "100%",
        minHeight: "92px",
        borderRadius: "10px",
        overflow: "hidden",
        border: `1px solid ${REPORT_COLORS.line}`,
        background: "#fff",
        position: "relative",
      }}
    >
      {status === "ready" || status === "loading" ? (
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "92px",
            display: "block",
            opacity: status === "ready" ? 1 : 0.35,
          }}
        />
      ) : (
        <div
          style={{
            minHeight: "92px",
            display: "grid",
            placeItems: "center",
            padding: "10px",
            textAlign: "center",
            color: REPORT_COLORS.inkSoft,
            fontSize: "11px",
            lineHeight: 1.4,
          }}
        >
          {status === "unavailable"
            ? "Document preview unavailable"
            : "Preview could not load"}
        </div>
      )}
      {status === "loading" ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            color: REPORT_COLORS.inkSoft,
            fontSize: "11px",
            background: "rgba(255,255,255,0.55)",
          }}
        >
          Loading page...
        </div>
      ) : null}
    </div>
  );
};

const FraudPdfOverlayPage = ({ pdfDocument, pageNumber, findings }) => {
  const canvasRef = useRef(null);
  const [pageState, setPageState] = useState({
    status: "loading",
    width: 0,
    height: 0,
    overlays: [],
  });

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) return undefined;

    let cancelled = false;
    let renderTask = null;

    const renderPage = async () => {
      try {
        setPageState((current) => ({ ...current, status: "loading" }));
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1.45 });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;

        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);

        renderTask = page.render({
          canvasContext: context,
          viewport,
        });
        await renderTask.promise;

        if (cancelled) return;

        setPageState({
          status: "ready",
          width: viewport.width,
          height: viewport.height,
          overlays: findings.map((finding) => ({
            ...getRenderedBboxRect(viewport, finding),
            id: finding.id,
            label: finding.label,
            textSample: finding.textSample,
          })),
        });
      } catch (error) {
        if (!cancelled) {
          setPageState({
            status: "error",
            width: 0,
            height: 0,
            overlays: [],
          });
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTask?.cancel) renderTask.cancel();
    };
  }, [pdfDocument, pageNumber, findings]);

  return (
    <div style={{ marginTop: "18px" }}>
      <div
        style={{
          ...labelStyle,
          marginBottom: "8px",
          color: REPORT_COLORS.inkSoft,
        }}
      >
        Page {pageNumber}
      </div>
      <div
        style={{
          border: `1px solid ${REPORT_COLORS.line}`,
          borderRadius: "14px",
          background: "#f8fafc",
          overflow: "auto",
          padding: "12px",
        }}
      >
        {pageState.status === "error" ? (
          <div
            style={{
              padding: "28px",
              color: REPORT_COLORS.inkSoft,
              textAlign: "center",
              fontSize: "13px",
            }}
          >
            This page could not be rendered in the preview.
          </div>
        ) : (
          <div
            style={{
              width: pageState.width || "100%",
              height: pageState.height || 420,
              position: "relative",
              margin: "0 auto",
              background: "#fff",
              boxShadow: "0 8px 26px rgba(15,23,42,0.08)",
            }}
          >
            <canvas ref={canvasRef} style={{ display: "block" }} />
            {pageState.overlays.map((overlay) => (
              <div
                key={overlay.id}
                title={[
                  overlay.label,
                  overlay.textSample ? `Sample: ${overlay.textSample}` : null,
                ]
                  .filter(Boolean)
                  .join(" - ")}
                style={{
                  position: "absolute",
                  left: `${overlay.left}px`,
                  top: `${overlay.top}px`,
                  width: `${Math.max(overlay.width, 3)}px`,
                  height: `${Math.max(overlay.height, 3)}px`,
                  border: "2px solid #dc2626",
                  background: "rgba(220,38,38,0.12)",
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.8)",
                  pointerEvents: "none",
                }}
              />
            ))}
            {pageState.status === "loading" ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  color: REPORT_COLORS.inkSoft,
                  background: "rgba(255,255,255,0.72)",
                  fontSize: "13px",
                }}
              >
                Loading page...
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const FraudBboxPreviewModal = ({ preview, onClose, isMobile }) => {
  const [pdfState, setPdfState] = useState({
    status: "idle",
    document: null,
    error: "",
  });

  useEffect(() => {
    if (!preview?.documentUrl) {
      setPdfState({
        status: preview ? "unavailable" : "idle",
        document: null,
        error: "",
      });
      return undefined;
    }

    let cancelled = false;
    let loadingTask = null;
    let loadedDocument = null;

    const loadDocument = async () => {
      try {
        setPdfState({ status: "loading", document: null, error: "" });
        loadingTask = pdfjsLib.getDocument(preview.documentUrl);
        loadedDocument = await loadingTask.promise;

        if (!cancelled) {
          setPdfState({
            status: "ready",
            document: loadedDocument,
            error: "",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPdfState({
            status: "error",
            document: null,
            error:
              error?.message ||
              "The document could not be rendered in this preview.",
          });
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      if (loadingTask?.destroy) loadingTask.destroy();
      if (loadedDocument?.destroy) loadedDocument.destroy();
    };
  }, [preview]);

  const findingsByPage = useMemo(() => {
    const grouped = new Map();

    (preview?.findings || []).forEach((finding) => {
      if (!grouped.has(finding.pageNumber)) {
        grouped.set(finding.pageNumber, []);
      }
      grouped.get(finding.pageNumber).push(finding);
    });

    return Array.from(grouped.entries()).sort(([pageA], [pageB]) => pageA - pageB);
  }, [preview]);

  if (!preview) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "center",
        padding: isMobile ? "0" : "24px",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          width: isMobile ? "100%" : "min(1040px, 96vw)",
          maxHeight: isMobile ? "100%" : "92vh",
          background: "#fff",
          borderRadius: isMobile ? 0 : "18px",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(15,23,42,0.28)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "16px 18px",
            borderBottom: `1px solid ${REPORT_COLORS.line}`,
            display: "flex",
            gap: "12px",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 800,
                color: REPORT_COLORS.ink,
                wordBreak: "break-word",
              }}
            >
              {preview.fileName || "Document fraud preview"}
            </div>
            <div
              style={{
                marginTop: "4px",
                fontSize: "12px",
                color: REPORT_COLORS.inkSoft,
              }}
            >
              Red boxes show detected fraud-check bbox locations.
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
            {preview.documentUrl ? (
              <a
                href={preview.documentUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  ...secondaryButtonStyle,
                  textDecoration: "none",
                  padding: "8px 10px",
                }}
              >
                Open PDF
              </a>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              style={{
                ...actionButtonStyle,
                padding: "8px 10px",
                background: REPORT_COLORS.ink,
                borderColor: REPORT_COLORS.ink,
              }}
            >
              Close
            </button>
          </div>
        </div>

        <div style={{ padding: "16px 18px", overflow: "auto" }}>
          {pdfState.status === "loading" ? (
            <div
              style={{
                padding: "36px",
                textAlign: "center",
                color: REPORT_COLORS.inkSoft,
                fontSize: "13px",
              }}
            >
              Loading document preview...
            </div>
          ) : null}

          {["error", "unavailable"].includes(pdfState.status) ? (
            <div
              style={{
                padding: "32px",
                border: `1px dashed ${REPORT_COLORS.line}`,
                borderRadius: "14px",
                color: REPORT_COLORS.inkSoft,
                textAlign: "center",
                fontSize: "13px",
              }}
            >
              {pdfState.error ||
                "The original document URL is not available for this fraud check."}
            </div>
          ) : null}

          {pdfState.status === "ready" && pdfState.document
            ? findingsByPage.map(([pageNumber, findings]) => (
                <FraudPdfOverlayPage
                  key={pageNumber}
                  pdfDocument={pdfState.document}
                  pageNumber={pageNumber}
                  findings={findings}
                />
              ))
            : null}
        </div>
      </div>
    </div>
  );
};

const formatCommitmentDates = (item) => {
  const dates = Array.isArray(item?.transaction_dates)
    ? item.transaction_dates
    : item?.source_transaction?.date
      ? [item.source_transaction.date]
      : [];

  const formattedDates = dates
    .filter(Boolean)
    .map((date) => formatDateLabel(date))
    .filter(Boolean);

  if (formattedDates.length === 0) {
    return "-";
  }

  if (formattedDates.length <= 3) {
    return formattedDates.join(", ");
  }

  return `${formattedDates.slice(0, 3).join(", ")} +${formattedDates.length - 3}`;
};

const TransactionPicker = ({
  mode,
  title,
  transactions,
  selectedIds,
  currency,
  onAdd,
  onClose,
  isMobile,
}) => {
  const [query, setQuery] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [rangeLabel, setRangeLabel] = useState("Any amount");
  const [pageIndex, setPageIndex] = useState(0);
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const searchText = normalizeSearchText(query);
  const numericMin = Number(minAmount);
  const numericMax = Number(maxAmount);

  const filteredTransactions = useMemo(() => {
    return (transactions || [])
      .filter((transaction) => transaction.direction === mode)
      .filter((transaction) => !selectedSet.has(transaction.id))
      .filter((transaction) => {
        const amount = Number(transaction.absolute_amount);
        if (Number.isFinite(numericMin) && minAmount !== "" && amount < numericMin) {
          return false;
        }
        if (Number.isFinite(numericMax) && maxAmount !== "" && amount > numericMax) {
          return false;
        }
        if (!searchText) {
          return true;
        }

        return [
          transaction.description,
          transaction.counterparty,
          transaction.bank,
          transaction.account_number,
          transaction.month_label,
          transaction.category,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(searchText);
    });
  }, [transactions, mode, selectedSet, numericMin, numericMax, minAmount, maxAmount, searchText]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / TRANSACTION_PICKER_PAGE_SIZE),
  );
  const pageStartIndex = pageIndex * TRANSACTION_PICKER_PAGE_SIZE;
  const visibleTransactions = filteredTransactions.slice(
    pageStartIndex,
    pageStartIndex + TRANSACTION_PICKER_PAGE_SIZE,
  );
  const displayStart =
    filteredTransactions.length === 0 ? 0 : pageStartIndex + 1;
  const displayEnd = pageStartIndex + visibleTransactions.length;
  const canGoPrevious = pageIndex > 0;
  const canGoNext = pageIndex < totalPages - 1;

  useEffect(() => {
    setPageIndex(0);
  }, [mode, query, minAmount, maxAmount, rangeLabel]);

  useEffect(() => {
    setPageIndex((currentPageIndex) =>
      Math.min(currentPageIndex, totalPages - 1),
    );
  }, [totalPages]);

  const handleRangeChange = (event) => {
    const option = amountRangeOptions.find(
      (item) => item.label === event.target.value,
    );
    setRangeLabel(option?.label || "Any amount");
    setMinAmount(option?.min || "");
    setMaxAmount(option?.max || "");
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(11,31,58,0.46)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "center",
        padding: isMobile ? 0 : "24px",
      }}
    >
      <div
        style={{
          width: isMobile ? "100%" : "min(980px, 96vw)",
          maxHeight: isMobile ? "100%" : "86vh",
          background: "#fff",
          borderRadius: isMobile ? 0 : "14px",
          boxShadow: "0 24px 70px rgba(11,31,58,0.28)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 20px", borderBottom: `1px solid ${REPORT_COLORS.line}` }}>
          <div style={summaryRowStyle}>
            <div>
              <div style={labelStyle}>{mode === "debit" ? "Debit transactions" : "Credit transactions"}</div>
              <div style={{ fontSize: "22px", fontWeight: 800, marginTop: "4px" }}>{title}</div>
            </div>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>
              Close
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "minmax(220px, 1fr) 170px 130px 130px",
              gap: "10px",
              marginTop: "14px",
            }}
          >
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search description, bank, account"
              style={filterInputStyle}
            />
            <select value={rangeLabel} onChange={handleRangeChange} style={filterInputStyle}>
              {rangeLabel === "Custom" ? (
                <option value="Custom">Custom</option>
              ) : null}
              {amountRangeOptions.map((option) => (
                <option key={option.label} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              value={minAmount}
              onChange={(event) => {
                setMinAmount(event.target.value);
                setRangeLabel("Custom");
              }}
              placeholder="Min"
              inputMode="decimal"
              style={filterInputStyle}
            />
            <input
              value={maxAmount}
              onChange={(event) => {
                setMaxAmount(event.target.value);
                setRangeLabel("Custom");
              }}
              placeholder="Max"
              inputMode="decimal"
              style={filterInputStyle}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              marginTop: "10px",
              fontSize: "12px",
              color: REPORT_COLORS.inkSoft,
            }}
          >
            <span>
              Showing {displayStart}-{displayEnd} of{" "}
              {filteredTransactions.length} matching transactions.
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                onClick={() =>
                  setPageIndex((currentPageIndex) =>
                    Math.max(currentPageIndex - 1, 0),
                  )
                }
                disabled={!canGoPrevious}
                style={{
                  ...secondaryButtonStyle,
                  padding: "7px 10px",
                  opacity: canGoPrevious ? 1 : 0.45,
                  cursor: canGoPrevious ? "pointer" : "not-allowed",
                }}
              >
                Previous
              </button>
              <span>
                Page {Math.min(pageIndex + 1, totalPages)} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPageIndex((currentPageIndex) =>
                    Math.min(currentPageIndex + 1, totalPages - 1),
                  )
                }
                disabled={!canGoNext}
                style={{
                  ...secondaryButtonStyle,
                  padding: "7px 10px",
                  opacity: canGoNext ? 1 : 0.45,
                  cursor: canGoNext ? "pointer" : "not-allowed",
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div style={{ overflow: "auto", padding: "0 20px 20px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={tableHeaderCellStyle}>Date</th>
                <th style={tableHeaderCellStyle}>Description</th>
                <th style={tableHeaderCellStyle}>Account</th>
                <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>Amount</th>
                <th style={tableHeaderCellStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleTransactions.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...tableCellStyle, color: REPORT_COLORS.inkSoft }}>
                    No transactions match the current filters.
                  </td>
                </tr>
              ) : (
                visibleTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td style={tableCellStyle}>{formatDateLabel(transaction.date)}</td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: 700 }}>
                        {transaction.description || transaction.counterparty || "-"}
                      </div>
                      <div style={{ color: REPORT_COLORS.inkSoft, fontSize: "11px", marginTop: "4px" }}>
                        {transaction.month_label || "-"}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      {[transaction.bank, transaction.account_number].filter(Boolean).join(" · ") || "-"}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: "right", fontWeight: 800 }}>
                      {formatMoney(transaction.absolute_amount, transaction.currency || currency || "SGD", 2)}
                    </td>
                    <td style={tableCellStyle}>
                      <button
                        type="button"
                        onClick={() => onAdd(transaction)}
                        style={{ ...secondaryButtonStyle, padding: "7px 10px" }}
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ScoreDial = ({ score }) => {
  const numericScore = Number(score) || 0;
  const circumference = 314;
  const band =
    numericScore >= 75 ? "green" : numericScore >= 55 ? "amber" : "red";
  const colors = bandColors(band);

  return (
    <svg
      viewBox="0 0 120 120"
      style={{ width: "136px", height: "136px", flexShrink: 0 }}
    >
      <circle
        cx="60"
        cy="60"
        r="50"
        fill="none"
        stroke="#eef2f7"
        strokeWidth="12"
      />
      <circle
        cx="60"
        cy="60"
        r="50"
        fill="none"
        stroke={colors.text}
        strokeWidth="12"
        strokeDasharray={circumference}
        strokeDashoffset={circumference - (circumference * numericScore) / 100}
        strokeLinecap="round"
        transform="rotate(-90 60 60)"
      />
      <text
        x="60"
        y="58"
        textAnchor="middle"
        fontSize="28"
        fontWeight="800"
        fill={REPORT_COLORS.ink}
      >
        {numericScore}
      </text>
      <text
        x="60"
        y="76"
        textAnchor="middle"
        fontSize="9"
        fill={REPORT_COLORS.inkSoft}
      >
        / 100
      </text>
    </svg>
  );
};

const RatioMeter = ({
  title,
  subtitle,
  value,
  band,
  scaleMax,
  ghostValue,
  formatter,
  reverse = false,
}) => {
  const colors = bandColors(band);
  const needlePosition = clamp((Number(value) / scaleMax) * 100, 0, 100);
  const ghostPosition = clamp((Number(ghostValue) / scaleMax) * 100, 0, 100);
  const background = reverse
    ? `linear-gradient(90deg, ${REPORT_COLORS.red} 0 20%, #f5b740 20% 40%, ${REPORT_COLORS.green} 40% 100%)`
    : `linear-gradient(90deg, ${REPORT_COLORS.green} 0 40%, #f5b740 40% 70%, ${REPORT_COLORS.red} 70% 100%)`;

  return (
    <div style={{ marginTop: "14px" }}>
      <div style={summaryRowStyle}>
        <div>
          <div
            style={{
              fontSize: "13px",
              fontWeight: 700,
              color: REPORT_COLORS.ink,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: REPORT_COLORS.inkSoft,
              marginTop: "2px",
            }}
          >
            {subtitle}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{ fontSize: "24px", fontWeight: 800, color: colors.text }}
          >
            {formatter(value)}
          </div>
          <div style={pillStyle(band)}>
            {title === "DSR"
              ? getDsrLabel(Number(value))
              : getFccLabel(Number(value))}
          </div>
        </div>
      </div>
      <div
        style={{
          position: "relative",
          height: "14px",
          borderRadius: "999px",
          overflow: "hidden",
          marginTop: "10px",
          background,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-3px",
            bottom: "-3px",
            width: "3px",
            background: "rgba(255,255,255,0.86)",
            left: `${ghostPosition}%`,
            opacity: 0.45,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-4px",
            bottom: "-4px",
            width: "4px",
            background: REPORT_COLORS.ink,
            left: `${needlePosition}%`,
            borderRadius: "999px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        />
      </div>
    </div>
  );
};

const Sparkline = ({ series, currency }) => {
  const values = (series || []).map((item) => Number(item.end_balance) || 0);
  if (values.length === 0) {
    return (
      <div style={{ color: REPORT_COLORS.inkSoft, fontSize: "13px" }}>
        No month-end balance series yet.
      </div>
    );
  }

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const points = values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * 240;
      const y = 58 - ((value - min) / span) * 32;
      return `${x},${y}`;
    })
    .join(" ");

  const area = `M0,80 L${points.split(" ").join(" L")} L240,80 Z`;

  return (
    <div>
      <svg
        viewBox="0 0 240 80"
        style={{ width: "100%", height: "86px", marginTop: "10px" }}
      >
        <defs>
          <linearGradient id="reportSparklineFill" x1="0" x2="0" y1="0" y2="1">
            <stop
              offset="0%"
              stopColor={REPORT_COLORS.blue}
              stopOpacity="0.3"
            />
            <stop
              offset="100%"
              stopColor={REPORT_COLORS.blue}
              stopOpacity="0"
            />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#reportSparklineFill)" />
        <polyline
          fill="none"
          stroke={REPORT_COLORS.blue}
          strokeWidth="2.5"
          points={points}
        />
        {(series || []).map((item, index) => (
          <text
            key={item.month_key || item.month_label || index}
            x={(index / Math.max(values.length - 1, 1)) * 240}
            y="76"
            fontSize="8"
            fill={REPORT_COLORS.inkSoft}
            textAnchor={
              index === 0
                ? "start"
                : index === values.length - 1
                  ? "end"
                  : "middle"
            }
          >
            {formatShortMonth(item.month_label)}
          </text>
        ))}
      </svg>
      <div style={{ fontSize: "11px", color: REPORT_COLORS.inkSoft }}>
        Primary operating currency:{" "}
        <strong style={{ color: REPORT_COLORS.ink }}>
          {currency || "SGD"}
        </strong>
      </div>
    </div>
  );
};

const DonutChart = ({ segments }) => {
  const total =
    segments.reduce((sum, item) => sum + Number(item.sharePercent || 0), 0) ||
    100;
  let offset = 0;

  return (
    <svg
      viewBox="0 0 120 120"
      style={{ width: "150px", height: "150px", flexShrink: 0 }}
    >
      <circle
        cx="60"
        cy="60"
        r="50"
        fill="none"
        stroke="#eef2f7"
        strokeWidth="18"
      />
      {segments.map((item) => {
        const length = (314 * Number(item.sharePercent || 0)) / total;
        const circle = (
          <circle
            key={item.label}
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke={item.color}
            strokeWidth="18"
            strokeDasharray={`${length} 314`}
            strokeDashoffset={-offset}
            transform="rotate(-90 60 60)"
          />
        );
        offset += length;
        return circle;
      })}
    </svg>
  );
};

const Drilldown = ({ title, subtitle, children, defaultOpen = true }) => (
  <details open={defaultOpen} style={{ ...cardStyle, padding: 0 }}>
    <summary
      style={{
        ...summaryRowStyle,
        cursor: "pointer",
        listStyle: "none",
        padding: "18px 20px",
      }}
    >
      <div
        style={{ fontSize: "16px", fontWeight: 700, color: REPORT_COLORS.ink }}
      >
        {title}
      </div>
      <div style={{ fontSize: "12px", color: REPORT_COLORS.inkSoft }}>
        {subtitle}
      </div>
    </summary>
    <div style={{ padding: "0 20px 20px" }}>{children}</div>
  </details>
);

const BankStatementGroupReport = ({
  report,
  groupName,
  reportRef,
  isMobile = false,
  cbsObligations = [],
  cbsMonthlyInstalmentHistories = [],
  hideFraudChecks = false,
  hideCbsSection = false,
  savedScenario = null,
  onScenarioChange,
  savingScenario = false,
}) => {
  // const isMobile = useMediaQuery("(max-width:600px)");
  const dashboard = report?.dashboard || {};
  const overview = report?.overview || {};
  const assessment = report?.credit_assessment || {};
  const topBar = dashboard.top_bar || {};
  const cashPosition = dashboard.cash_position || {};
  const drilldowns = dashboard.drilldowns || {};
  const heatmap = dashboard.heatmap || {};
  const fraudChecks = Array.isArray(report?.fraud_checks)
    ? report.fraud_checks
    : [];
  const cbsRows = Array.isArray(cbsObligations) ? cbsObligations : [];
  const cbsHistories = Array.isArray(cbsMonthlyInstalmentHistories)
    ? cbsMonthlyInstalmentHistories
    : [];
  const [fraudPreview, setFraudPreview] = useState(null);
  const monthlyDetailRows = useMemo(
    () =>
      Array.isArray(drilldowns.month_detail) ? drilldowns.month_detail : [],
    [drilldowns.month_detail],
  );
  const monthlyDetailTotal = useMemo(() => {
    const sumField = (field) => {
      const values = monthlyDetailRows
        .map((item) => Number(item?.[field]))
        .filter(Number.isFinite);

      if (values.length === 0) {
        return null;
      }

      return values.reduce((sum, value) => sum + value, 0);
    };

    const lowestDay = monthlyDetailRows.reduce((lowest, item) => {
      const balance = Number(item?.lowest_day_balance);

      if (!Number.isFinite(balance)) {
        return lowest;
      }

      if (!lowest || balance < lowest.balance) {
        return {
          balance,
          date: item?.lowest_day_date || null,
        };
      }

      return lowest;
    }, null);

    return {
      deposits: sumField("deposits"),
      withdrawals: sumField("withdrawals"),
      net_cash_flow: sumField("net_cash_flow"),
      end_balance: sumField("end_balance"),
      lowest_day_balance: lowestDay?.balance ?? null,
      lowest_day_date: lowestDay?.date ?? null,
    };
  }, [monthlyDetailRows]);

  const scenarioSeed = useMemo(
    () =>
      JSON.stringify({
        commitments: (dashboard.commitments || []).map((item) => [
          item.id,
          item.keep,
        ]),
        lumpSums: (dashboard.lump_sum_deposits?.items || []).map((item) => [
          item.id,
          item.include_in_revenue,
        ]),
        defaults: dashboard.coverage?.stress_defaults || {},
        savedScenario,
      }),
    [dashboard, savedScenario],
  );
  const scenarioSaveRef = useRef(false);
  const [pickerMode, setPickerMode] = useState(null);
  const [manualDsrForm, setManualDsrForm] = useState({
    title: "",
    amount: "",
  });
  const [manualRevenueForm, setManualRevenueForm] = useState({
    title: "",
    amount: "",
  });

  const [scenario, setScenario] = useState(() =>
    buildInitialReportScenario(report, savedScenario),
  );

  useEffect(() => {
    scenarioSaveRef.current = false;
    setScenario(buildInitialReportScenario(report, savedScenario));
  }, [report, savedScenario, scenarioSeed]);

  useEffect(() => {
    if (!scenarioSaveRef.current) {
      scenarioSaveRef.current = true;
      return undefined;
    }

    if (!onScenarioChange) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      onScenarioChange(scenario);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [scenario, onScenarioChange]);

  const live = useMemo(
    () => deriveReportScenario(report, scenario),
    [report, scenario],
  );
  const displayDsr =
    live.stress.loanAmount > 0
      ? live.stressBreakdown.dsrPercent
      : live.baseDsrPercent;
  const displayFcc =
    live.stress.loanAmount > 0
      ? live.stressBreakdown.fccRatio
      : live.baseFccRatio;
  const displayDsrBand = getDsrBand(displayDsr);
  const displayFccBand = getFccBand(displayFcc);
  const displayRatingBand =
    assessment.rating === "Strong"
      ? "green"
      : assessment.rating === "Moderate"
        ? "amber"
        : "red";
  const liveLenderExposure = live.activeCommitments
    .filter((item) => item.likely_lender)
    .map((item) => ({
      counterparty: item.counterparty,
      monthly_outflow: item.monthly_amount,
      months_seen: item.months_seen,
      notes: item.notes,
    }));
  const recurringMonthlyInflow =
    Number(drilldowns.operating_income?.recurring_monthly_inflow) || 0;
  const oneOffMonthlyInflow = Math.max(
    0,
    live.adjustedMonthlyRevenue - recurringMonthlyInflow,
  );
  const pickerTransactions = Array.isArray(report?.transaction_picker)
    ? report.transaction_picker
    : [];
  const selectedManualCommitmentIds = (scenario.manualCommitments || []).map(
    (item) => item.transaction_id,
  );
  const selectedManualLumpSumIds = (scenario.manualLumpSums || []).map(
    (item) => item.transaction_id,
  );

  const resetDsrSelection = () => {
    setScenario((current) => ({
      ...current,
      commitments: Object.fromEntries(
        (dashboard.commitments || []).map((item) => [item.id, false]),
      ),
      manualCommitments: [],
    }));
  };

  const resetRevenueSelection = () => {
    setScenario((current) => ({
      ...current,
      lumpSums: Object.fromEntries(
        (dashboard.lump_sum_deposits?.items || []).map((item) => [
          item.id,
          false,
        ]),
      ),
      manualLumpSums: [],
    }));
  };

  const updateCommitment = (id) => {
    setScenario((current) => {
      if ((current.manualCommitments || []).some((item) => item.id === id)) {
        return {
          ...current,
          manualCommitments: (current.manualCommitments || []).map((item) =>
            item.id === id
              ? {
                  ...item,
                  active: item.active === false,
                  keep: item.active === false,
                }
              : item,
          ),
        };
      }

      return {
        ...current,
        commitments: {
          ...current.commitments,
          [id]: !current.commitments[id],
        },
      };
    });
  };

  const updateLumpSum = (id) => {
    setScenario((current) => {
      if ((current.manualLumpSums || []).some((item) => item.id === id)) {
        return {
          ...current,
          manualLumpSums: (current.manualLumpSums || []).map((item) =>
            item.id === id
              ? {
                  ...item,
                  included: item.included === false,
                  include_in_revenue: item.included === false,
                }
              : item,
          ),
        };
      }

      return {
        ...current,
        lumpSums: {
          ...current.lumpSums,
          [id]: !current.lumpSums[id],
        },
      };
    });
  };

  const buildTypedManualTransaction = (form, mode) => {
    const title = String(form.title || "").trim();
    const amount = Math.abs(Number(form.amount));

    if (!title || !Number.isFinite(amount) || amount <= 0) {
      return null;
    }

    return {
      id: `typed-${mode}-${Date.now()}`,
      date: new Date().toISOString().slice(0, 10),
      amount: mode === "debit" ? -amount : amount,
      currency: overview.primary_currency || "SGD",
      description: title,
      counterparty: title,
      bank: "Manual entry",
      month_label: "Manual entry",
    };
  };

  const addManualCommitment = (transaction) => {
    setScenario((current) => {
      if (
        (current.manualCommitments || []).some(
          (item) => item.transaction_id === transaction.id,
        )
      ) {
        return current;
      }

      return {
        ...current,
        manualCommitments: [
          ...(current.manualCommitments || []),
          buildManualCommitmentFromTransaction(transaction),
        ],
      };
    });
  };

  const addTypedManualCommitment = () => {
    const transaction = buildTypedManualTransaction(manualDsrForm, "debit");

    if (!transaction) {
      return;
    }

    addManualCommitment(transaction);
    setManualDsrForm({ title: "", amount: "" });
  };

  const removeManualCommitment = (id) => {
    setScenario((current) => ({
      ...current,
      manualCommitments: (current.manualCommitments || []).filter(
        (item) => item.id !== id,
      ),
    }));
  };

  const addManualLumpSum = (transaction) => {
    setScenario((current) => {
      if (
        (current.manualLumpSums || []).some(
          (item) => item.transaction_id === transaction.id,
        )
      ) {
        return current;
      }

      return {
        ...current,
        manualLumpSums: [
          ...(current.manualLumpSums || []),
          buildManualLumpSumFromTransaction(transaction),
        ],
      };
    });
  };

  const addTypedManualLumpSum = () => {
    const transaction = buildTypedManualTransaction(
      manualRevenueForm,
      "credit",
    );

    if (!transaction) {
      return;
    }

    addManualLumpSum(transaction);
    setManualRevenueForm({ title: "", amount: "" });
  };

  const removeManualLumpSum = (id) => {
    setScenario((current) => ({
      ...current,
      manualLumpSums: (current.manualLumpSums || []).filter(
        (item) => item.id !== id,
      ),
    }));
  };

  const updateStress = (key, value) => {
    setScenario((current) => ({
      ...current,
      stress: {
        ...current.stress,
        [key]: value,
      },
    }));
  };

  const heatMonthCount = Math.max((heatmap.months || []).length, 1);
  const heatCellSize = isMobile
    ? Math.max(5, Math.min(10, Math.floor(132 / heatMonthCount)))
    : Math.max(7, Math.min(16, Math.floor(230 / heatMonthCount)));
  const heatColumnGap = isMobile
    ? Math.max(4, Math.min(14, Math.floor(58 / heatMonthCount)))
    : Math.max(5, Math.min(18, Math.floor(106 / heatMonthCount)));
  const heatDayGap = Math.max(2, Math.min(isMobile ? 5 : 4, Math.floor(heatCellSize / 4)));
  const heatMonthLabelSize = Math.max(isMobile ? 8 : 9, Math.min(isMobile ? 11 : 15, heatCellSize + 1));
  const heatColumnWidth = Math.max(heatCellSize, Math.ceil(heatMonthLabelSize * 2.8));
  const heatLowLabelSize = Math.max(isMobile ? 8 : 9, Math.min(isMobile ? 11 : 14, heatCellSize));
  const waterfallItems = drilldowns.waterfall || [];
  const waterfallGlobalMax = Math.max(
    ...waterfallItems.flatMap((item) => [
      Number(item.opening_balance) || 0,
      Number(item.deposits) || 0,
      Number(item.withdrawals) || 0,
      Number(item.closing_balance) || 0,
    ]),
    1,
  );
  const waterfallBarWidth = isMobile ? 16 : 20;

  return (
    <div
      ref={reportRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        background: REPORT_COLORS.bg,
        borderRadius: "28px",
        overflow: "hidden",
        border: `1px solid ${REPORT_COLORS.line}`,
        color: REPORT_COLORS.ink,
      }}
    >
      <div
        style={{
          padding: isMobile ? "18px 16px" : "18px 24px",
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${REPORT_COLORS.line}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "18px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: "240px",
            }}
          >
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "12px",
                background: REPORT_COLORS.blue,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
              }}
            >
              S
            </div>
            <div>
              <div style={labelStyle}>SmartLend · Lender View</div>
              <div style={{ fontSize: "15px", fontWeight: 700 }}>
                {overview.group_name || groupName || "Bank Statement Portal"}
              </div>
              <div style={{ fontSize: "12px", color: REPORT_COLORS.inkSoft }}>
                {overview.primary_currency || "SGD"} ·{" "}
                {overview.months_covered_count || 0} months ·{" "}
                {overview.account_count || 0} account(s)
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "18px",
              flexWrap: "wrap",
              flex: 1,
            }}
          >
            {[
              { label: "Score", value: `${topBar.score ?? "-"} / 100` },
              { label: "DSR", value: formatPercent(displayDsr) },
              { label: "FCC", value: formatMultiple(displayFcc) },
              {
                label: "Buffer",
                value: `${Number(topBar.buffer_days || 0).toFixed(1)}d`,
              },
              { label: "Bounces", value: topBar.bounce_count ?? 0 },
            ].map((item) => (
              <div key={item.label}>
                <div style={labelStyle}>{item.label}</div>
                <div style={{ fontSize: "18px", fontWeight: 800 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginLeft: "auto",
              flexWrap: "wrap",
            }}
          >
            <div style={pillStyle(displayRatingBand)}>
              {assessment.rating || "Moderate"}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: isMobile ? "16px" : "24px",
          display: "grid",
          gap: "18px",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <section
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "minmax(0,1fr) minmax(0,1.15fr) minmax(0,1fr)",
            gap: "18px",
          }}
        >
          <div style={cardStyle}>
            <div style={summaryRowStyle}>
              <div style={labelStyle}>Credit profile</div>
              <div style={pillStyle(displayRatingBand)}>
                {assessment.rating || "Moderate"}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "18px",
                alignItems: "center",
                marginTop: "14px",
                flexWrap: "wrap",
              }}
            >
              <ScoreDial score={assessment.score} />
              <div style={{ display: "grid", gap: "10px", flex: 1 }}>
                <div style={pillStyle(displayDsrBand)}>
                  Repayment: {assessment.repayment_capacity || "Watchlist"}
                </div>
                <div
                  style={pillStyle(
                    topBar.buffer_days > 3
                      ? "green"
                      : topBar.buffer_days > 1
                        ? "amber"
                        : "red",
                  )}
                >
                  Buffer: {Number(topBar.buffer_days || 0).toFixed(1)} days
                </div>
                <div
                  style={pillStyle(
                    (topBar.bounce_count || 0) === 0 ? "green" : "red",
                  )}
                >
                  Bounces: {topBar.bounce_count || 0}
                </div>
                <div
                  style={pillStyle(
                    cashPosition.deposit_volatility <= 1.25
                      ? "green"
                      : cashPosition.deposit_volatility <= 1.5
                        ? "amber"
                        : "red",
                  )}
                >
                  Deposit volatility:{" "}
                  {formatMultiple(cashPosition.deposit_volatility || 0)}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: "16px",
                fontSize: "13px",
                lineHeight: 1.7,
                color: REPORT_COLORS.inkSoft,
              }}
            >
              {assessment.summary}
            </div>
          </div>

          <div style={{ ...cardStyle, borderColor: "rgba(31, 95, 208, 0.16)" }}>
            <div style={summaryRowStyle}>
              <div style={labelStyle}>Coverage ratios · Live</div>
              <div style={{ fontSize: "11px", color: REPORT_COLORS.inkSoft }}>
                Guideline reference: keep DSR within policy tolerance
              </div>
            </div>

            <RatioMeter
              title="DSR"
              subtitle="Commitments ÷ adjusted revenue · lower is better"
              value={displayDsr}
              band={displayDsrBand}
              scaleMax={100}
              ghostValue={live.baseDsrPercent}
              formatter={(value) => formatPercent(value)}
            />

            <RatioMeter
              title="FCC"
              subtitle="Average balance ÷ monthly commitment · higher is better"
              value={displayFcc}
              band={displayFccBand}
              scaleMax={5}
              ghostValue={live.baseFccRatio}
              formatter={(value) => formatMultiple(value)}
              reverse
            />

            <div
              style={{
                marginTop: "18px",
                padding: "16px",
                borderRadius: "16px",
                background: "rgba(227, 237, 255, 0.45)",
                border: `1px solid ${REPORT_COLORS.blueSoft}`,
              }}
            >
              <div style={{ ...labelStyle, color: REPORT_COLORS.blue }}>
                Stress test a new facility
              </div>

              {[
                {
                  label: "Amount",
                  key: "loanAmount",
                  min: 0,
                  max: 500000,
                  step: 5000,
                  value: live.stress.loanAmount,
                  footer: formatMoney(
                    live.stress.loanAmount,
                    overview.primary_currency || "SGD",
                    0,
                  ),
                },
                {
                  label: "Tenor",
                  key: "loanTenorMonths",
                  min: 6,
                  max: 60,
                  step: 6,
                  value: live.stress.loanTenorMonths,
                  footer: `${live.stress.loanTenorMonths} mo`,
                },
                {
                  label: "Rate / mo",
                  key: "monthlyRatePercent",
                  min: 0.5,
                  max: 5,
                  step: 0.1,
                  value: live.stress.monthlyRatePercent,
                  footer: `${live.stress.monthlyRatePercent.toFixed(1)}% / mo`,
                },
              ].map((item) => (
                <div key={item.key} style={{ marginTop: "12px" }}>
                  <div style={summaryRowStyle}>
                    <div
                      style={{ fontSize: "12px", color: REPORT_COLORS.inkSoft }}
                    >
                      {item.label}
                    </div>
                    <div style={{ fontSize: "12px", fontWeight: 700 }}>
                      {item.footer}
                    </div>
                  </div>
                  <input
                    type="range"
                    min={item.min}
                    max={item.max}
                    step={item.step}
                    value={item.value}
                    onChange={(event) =>
                      updateStress(item.key, Number(event.target.value))
                    }
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      accentColor: REPORT_COLORS.blue,
                    }}
                  />
                </div>
              ))}

              <div
                style={{
                  marginTop: "14px",
                  fontSize: "11px",
                  color: REPORT_COLORS.inkSoft,
                  lineHeight: 1.6,
                }}
              >
                Monthly payment = Principal ÷ Tenor + Principal × Monthly rate
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  fontSize: "11px",
                  color: REPORT_COLORS.inkSoft,
                  marginTop: "10px",
                }}
              >
                <span>
                  Principal:{" "}
                  <strong style={{ color: REPORT_COLORS.ink }}>
                    {formatMoney(
                      live.stressBreakdown.principalPayment,
                      overview.primary_currency || "SGD",
                      0,
                    )}
                  </strong>
                </span>
                <span>
                  Interest:{" "}
                  <strong style={{ color: REPORT_COLORS.ink }}>
                    {formatMoney(
                      live.stressBreakdown.interestPayment,
                      overview.primary_currency || "SGD",
                      0,
                    )}
                  </strong>
                </span>
                <span>
                  Total:{" "}
                  <strong style={{ color: REPORT_COLORS.ink }}>
                    {formatMoney(
                      live.stressBreakdown.monthlyPayment,
                      overview.primary_currency || "SGD",
                      0,
                    )}
                  </strong>
                </span>
              </div>
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "12px",
                  color: REPORT_COLORS.inkSoft,
                }}
              >
                Headroom to 60% DSR:{" "}
                <strong style={{ color: REPORT_COLORS.ink }}>
                  {formatMoney(
                    live.headroomToSixtyDsr,
                    overview.primary_currency || "SGD",
                    0,
                  )}
                </strong>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={labelStyle}>Cash position</div>
            <Sparkline
              series={cashPosition.series || []}
              currency={overview.primary_currency}
            />
            <div
              style={{
                marginTop: "14px",
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "14px",
              }}
            >
              <div>
                <div style={labelStyle}>Lowest day</div>
                <div style={{ fontSize: "20px", fontWeight: 800 }}>
                  {formatMoney(
                    cashPosition.lowest_day_balance,
                    overview.primary_currency || "SGD",
                    0,
                  )}
                </div>
                <div style={{ fontSize: "11px", color: REPORT_COLORS.red }}>
                  {formatDateLabel(cashPosition.lowest_day_date)}
                </div>
              </div>
              <div>
                <div style={labelStyle}>Avg end-month</div>
                <div style={{ fontSize: "20px", fontWeight: 800 }}>
                  {formatMoney(
                    cashPosition.average_end_month_balance,
                    overview.primary_currency || "SGD",
                    0,
                  )}
                </div>
              </div>
              <div>
                <div style={labelStyle}>Months net positive</div>
                <div style={{ fontSize: "20px", fontWeight: 800 }}>
                  {cashPosition.months_net_positive || 0}
                  <span
                    style={{ fontSize: "13px", color: REPORT_COLORS.inkSoft }}
                  >
                    /{overview.months_covered_count || 0}
                  </span>
                </div>
              </div>
              <div>
                <div style={labelStyle}>Deposit volatility</div>
                <div style={{ fontSize: "20px", fontWeight: 800 }}>
                  {formatMultiple(cashPosition.deposit_volatility || 0)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {!hideFraudChecks && fraudChecks.length > 0 ? (
          <section style={cardStyle}>
            <div style={summaryRowStyle}>
              <div>
                <div style={labelStyle}>Document fraud checks</div>
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "13px",
                    color: REPORT_COLORS.inkSoft,
                  }}
                >
                  Scores are detected per uploaded bank statement. 0 is low risk;
                  100 is high risk.
                </div>
              </div>
              <div style={pillStyle(fraudBand(Math.max(...fraudChecks.map((item) => Number(item.risk_score) || 0))))}>
                Highest score:{" "}
                {Math.max(...fraudChecks.map((item) => Number(item.risk_score) || 0))}
                /100
              </div>
            </div>

            <div
              style={{
                marginTop: "16px",
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "12px",
              }}
            >
              {fraudChecks.map((item) => {
                const riskScore = Number(item.risk_score);
                const boundedScore = Number.isFinite(riskScore)
                  ? clamp(riskScore, 0, 100)
                  : null;
                const band = fraudBand(item.risk_level || boundedScore);
                const colors = bandColors(band);
                const flags = Array.isArray(item.flags) ? item.flags : [];

                return (
                  <div
                    key={`${item.job_id || item.file_name || "fraud"}-${item.account_number || ""}`}
                    style={{
                      border: `1px solid ${REPORT_COLORS.line}`,
                      borderRadius: "16px",
                      padding: "14px",
                      background: "rgba(255,255,255,0.72)",
                      minWidth: 0,
                    }}
                  >
                    <div style={summaryRowStyle}>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 800,
                            wordBreak: "break-word",
                          }}
                        >
                          {item.file_name || item.bank || "Bank statement"}
                        </div>
                        <div
                          style={{
                            marginTop: "4px",
                            fontSize: "12px",
                            color: REPORT_COLORS.inkSoft,
                          }}
                        >
                          {item.bank || "Unknown Bank"} ·{" "}
                          {item.account_number || "Unknown Account"} ·{" "}
                          {item.month_label || "-"}
                        </div>
                      </div>
                      <div style={pillStyle(band)}>
                        {item.risk_level || "Unknown"}
                      </div>
                    </div>

                    <div style={{ marginTop: "14px" }}>
                      <div style={summaryRowStyle}>
                        <div style={labelStyle}>Risk score</div>
                        <div style={{ fontSize: "13px", fontWeight: 800 }}>
                          {boundedScore === null ? "-" : `${boundedScore}/100`}
                        </div>
                      </div>
                      <div
                        style={{
                          marginTop: "8px",
                          height: "9px",
                          borderRadius: "999px",
                          background: REPORT_COLORS.line,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${boundedScore ?? 0}%`,
                            height: "100%",
                            background: colors.text,
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: "14px", display: "grid", gap: "8px" }}>
                      {flags.length > 0 ? (
                        flags.map((flag, index) => {
                          const flagBand = fraudBand(flag.severity);
                          const bboxFindings = extractFraudBboxFindings(
                            flag,
                            item,
                          );
                          return (
                            <div
                              key={`${flag.code || "flag"}-${index}`}
                              style={{
                                border: `1px solid ${REPORT_COLORS.line}`,
                                borderRadius: "12px",
                                padding: "10px",
                                background: "rgba(246,248,251,0.7)",
                              }}
                            >
                              <div style={summaryRowStyle}>
                                <div style={{ fontSize: "12px", fontWeight: 800 }}>
                                  {formatFraudFlagCode(flag.code)}
                                </div>
                                <div style={pillStyle(flagBand)}>
                                  {flag.severity || "medium"}
                                </div>
                              </div>
                              <div
                                style={{
                                  marginTop: "6px",
                                  fontSize: "12px",
                                  lineHeight: 1.5,
                                  color: REPORT_COLORS.inkSoft,
                                }}
                              >
                                {flag.message || "Fraud check flag detected."}
                              </div>
                              {flag.details &&
                              Object.keys(flag.details).length > 0 ? (
                                <div
                                  style={{
                                    marginTop: "8px",
                                    display: "flex",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {Object.entries(flag.details).map(
                                    ([key, value]) => (
                                      <span
                                        key={key}
                                        style={{
                                          padding: "4px 8px",
                                          borderRadius: "999px",
                                          background: "#fff",
                                          border: `1px solid ${REPORT_COLORS.line}`,
                                          color: REPORT_COLORS.inkSoft,
                                          fontSize: "11px",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {formatFraudFlagCode(key)}:{" "}
                                        {formatFraudDetailValue(key, value)}
                                      </span>
                                    ),
                                  )}
                                </div>
                              ) : null}
                              {bboxFindings.length > 0 ? (
                                <div style={{ marginTop: "10px" }}>
                                  <div
                                    style={{
                                      ...labelStyle,
                                      color: REPORT_COLORS.inkSoft,
                                      marginBottom: "7px",
                                    }}
                                  >
                                    Anomaly previews
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {bboxFindings.map((finding) => (
                                      <button
                                        key={finding.id}
                                        type="button"
                                        onClick={() =>
                                          setFraudPreview({
                                            documentUrl: finding.documentUrl,
                                            fileName: finding.fileName,
                                            findings: bboxFindings,
                                          })
                                        }
                                        style={{
                                          border: "none",
                                          padding: 0,
                                          background: "transparent",
                                          cursor: "pointer",
                                          textAlign: "left",
                                          color: REPORT_COLORS.ink,
                                          maxWidth: "190px",
                                        }}
                                      >
                                        <FraudBboxThumbnail finding={finding} />
                                        <div
                                          style={{
                                            marginTop: "5px",
                                            fontSize: "11px",
                                            color: REPORT_COLORS.inkSoft,
                                            lineHeight: 1.35,
                                          }}
                                        >
                                          Page {finding.pageNumber}
                                          {finding.textSample
                                            ? ` · ${finding.textSample}`
                                            : ""}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      ) : (
                        <div
                          style={{
                            fontSize: "12px",
                            color: REPORT_COLORS.inkSoft,
                          }}
                        >
                          No fraud flags detected for this statement.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {!hideCbsSection && (cbsRows.length > 0 || cbsHistories.length > 0) ? (
          <section style={cardStyle}>
            <div style={summaryRowStyle}>
              <div>
                <div style={labelStyle}>CBS detected obligations</div>
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "13px",
                    color: REPORT_COLORS.inkSoft,
                  }}
                >
                  These obligations and instalment trends are detected from CBS,
                  not from the bank statements.
                </div>
              </div>
              <div style={pillStyle("amber")}>CBS source</div>
            </div>

            {cbsHistories.length > 0 ? (
              <div
                style={{
                  marginTop: "16px",
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "12px",
                }}
              >
                {cbsHistories.map((history) => {
                  const rows = Array.isArray(history.rows) ? history.rows : [];
                  const maxValue = Math.max(
                    ...rows.map((row) => Number(row.monthly_instalment) || 0),
                    1,
                  );

                  return (
                    <div
                      key={history.id || history.report_name || "cbs-history"}
                      style={{
                        border: `1px solid ${REPORT_COLORS.line}`,
                        borderRadius: "16px",
                        padding: "14px",
                        background: "rgba(246,248,251,0.62)",
                        minWidth: 0,
                      }}
                    >
                      <div style={summaryRowStyle}>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 800,
                              wordBreak: "break-word",
                            }}
                          >
                            Last 6 months monthly instalment
                          </div>
                          <div
                            style={{
                              marginTop: "4px",
                              fontSize: "12px",
                              color: REPORT_COLORS.inkSoft,
                              wordBreak: "break-word",
                            }}
                          >
                            {history.report_name || "CBS report"} · Enquiry{" "}
                            {history.enquiry_date || "-"}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: "16px",
                          display: "grid",
                          gridTemplateColumns: `repeat(${Math.max(rows.length, 1)}, minmax(36px, 1fr))`,
                          gap: "8px",
                          alignItems: "end",
                          minHeight: "150px",
                        }}
                      >
                        {rows.map((row) => {
                          const amount = Number(row.monthly_instalment) || 0;
                          const height = Math.max(8, (amount / maxValue) * 112);

                          return (
                            <div
                              key={row.month_label}
                              style={{
                                display: "grid",
                                gap: "6px",
                                alignItems: "end",
                                minWidth: 0,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 800,
                                  textAlign: "center",
                                  color: REPORT_COLORS.ink,
                                  wordBreak: "break-word",
                                }}
                              >
                                {formatMoney(amount, "SGD", 2)}
                              </div>
                              <div
                                title={`${row.month_label}: ${formatMoney(amount, "SGD", 2)}`}
                                style={{
                                  height: `${height}px`,
                                  borderRadius: "8px 8px 3px 3px",
                                  background: REPORT_COLORS.blue,
                                  boxShadow: "0 10px 20px rgba(31,95,208,0.16)",
                                }}
                              />
                              <div
                                style={{
                                  minHeight: "28px",
                                  fontSize: "11px",
                                  textAlign: "center",
                                  color: REPORT_COLORS.inkSoft,
                                  wordBreak: "break-word",
                                }}
                              >
                                {row.month_label}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {cbsRows.length > 0 ? (
              <div style={{ marginTop: "16px", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderCellStyle}>CBS report</th>
                      <th style={tableHeaderCellStyle}>Grantor bank</th>
                      <th style={tableHeaderCellStyle}>Facility</th>
                      <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                        Outstanding balance
                      </th>
                      <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                        Monthly instalment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cbsRows.map((row) => (
                      <tr key={row.id}>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 800 }}>
                            {row.report_name || "CBS report"}
                          </div>
                          <div
                            style={{
                              marginTop: "4px",
                              color: REPORT_COLORS.inkSoft,
                              fontSize: "12px",
                            }}
                          >
                            Enquiry date {row.enquiry_date || "-"}
                          </div>
                        </td>
                        <td style={tableCellStyle}>{row.grantor_bank || "-"}</td>
                        <td style={tableCellStyle}>{row.facility || "CBS facility"}</td>
                        <td
                          style={{
                            ...tableCellStyle,
                            textAlign: "right",
                            fontWeight: 800,
                          }}
                        >
                          {formatMoney(row.outstanding_balance, "SGD", 2)}
                        </td>
                        <td
                          style={{
                            ...tableCellStyle,
                            textAlign: "right",
                            fontWeight: 800,
                          }}
                        >
                          {formatMoney(row.monthly_instalment, "SGD", 2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  marginTop: "14px",
                  fontSize: "13px",
                  color: REPORT_COLORS.inkSoft,
                }}
              >
                CBS report uploaded, but obligation rows were not detected from
                the extracted PDF text.
              </div>
            )}
          </section>
        ) : null}

        <section
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "minmax(0, 3fr) minmax(320px, 2fr)",
            gap: "18px",
          }}
        >
          <div style={cardStyle}>
            <div style={summaryRowStyle}>
              <div>
                <div style={labelStyle}>Detected commitments</div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 800,
                    marginTop: "6px",
                  }}
                >
                  Edit what counts toward DSR
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: REPORT_COLORS.inkSoft,
                    marginTop: "6px",
                    lineHeight: 1.6,
                  }}
                >
                  Automatic DSR candidates include recurring debits of at least
                  S$100 with the same amount across consecutive monthly payments
                  within a 5-day window, or any debit transaction with the word
                  loan or loar in the transaction description. You can still add
                  extra debit transactions manually.
                </div>
              </div>
              <div
                style={{
                  ...pillStyle("green"),
                  background: REPORT_COLORS.blueSoft,
                  color: REPORT_COLORS.blue,
                }}
              >
                {savingScenario ? "Saving..." : "Live recompute"}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginTop: "14px",
              }}
            >
              <button
                type="button"
                onClick={() => setPickerMode("dsr")}
                style={secondaryButtonStyle}
              >
                Add debit transaction
              </button>
              <button
                type="button"
                onClick={resetDsrSelection}
                style={resetButtonStyle}
              >
                Reset
              </button>
            </div>
            <div
              style={{
                ...manualEntryGridStyle,
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : manualEntryGridStyle.gridTemplateColumns,
              }}
            >
              <input
                type="text"
                value={manualDsrForm.title}
                onChange={(event) =>
                  setManualDsrForm((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
                placeholder="Manual DSR title"
                style={filterInputStyle}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={manualDsrForm.amount}
                onChange={(event) =>
                  setManualDsrForm((current) => ({
                    ...current,
                    amount: event.target.value,
                  }))
                }
                placeholder="Monthly amount"
                style={filterInputStyle}
              />
              <button
                type="button"
                onClick={addTypedManualCommitment}
                style={actionButtonStyle}
              >
                Add manual DSR
              </button>
            </div>

            <div style={{ overflowX: "auto", marginTop: "14px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={tableHeaderCellStyle}>Keep</th>
                    <th style={tableHeaderCellStyle}>Date</th>
                    <th style={tableHeaderCellStyle}>Counterparty</th>
                    <th style={tableHeaderCellStyle}>Cadence / Tag</th>
                    <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                      Avg / mo
                    </th>
                    <th
                      style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                    >
                      Months
                    </th>
                    <th
                      style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                    >
                      Confidence
                    </th>
                    <th style={tableHeaderCellStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(live.commitments || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          ...tableCellStyle,
                          color: REPORT_COLORS.inkSoft,
                        }}
                      >
                        No recurring commitment clusters were detected yet.
                      </td>
                    </tr>
                  ) : (
                    live.commitments.map((item) => (
                      <tr
                        key={item.id}
                        style={{ background: item.active ? "#fff" : "#fafcff" }}
                      >
                        <td style={tableCellStyle}>
                          <input
                            type="checkbox"
                            checked={item.active}
                            onChange={() => updateCommitment(item.id)}
                            style={{
                              width: "16px",
                              height: "16px",
                              accentColor: REPORT_COLORS.blue,
                            }}
                          />
                        </td>
                        <td style={tableCellStyle}>
                          {formatCommitmentDates(item)}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 700 }}>
                            {item.counterparty}
                          </div>
                          <div
                            style={{
                              marginTop: "4px",
                              fontSize: "11px",
                              color: REPORT_COLORS.inkSoft,
                            }}
                          >
                            {item.notes}
                          </div>
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 700 }}>{item.cadence}</div>
                          <div
                            style={{
                              marginTop: "4px",
                              fontSize: "11px",
                              color: REPORT_COLORS.inkSoft,
                            }}
                          >
                            {item.tag}
                          </div>
                        </td>
                        <td
                          style={{
                            ...tableCellStyle,
                            textAlign: "right",
                            fontWeight: 700,
                          }}
                        >
                          {formatMoney(
                            item.monthly_amount,
                            overview.primary_currency || "SGD",
                            0,
                          )}
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: "center" }}>
                          {item.months_seen}
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: "center" }}>
                          <span
                            style={pillStyle(
                              item.confidence >= 0.75
                                ? "green"
                                : item.confidence >= 0.55
                                  ? "amber"
                                  : "red",
                            )}
                          >
                            {item.confidence_label}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          {item.manual ? (
                            <button
                              type="button"
                              onClick={() => removeManualCommitment(item.id)}
                              style={{
                                ...secondaryButtonStyle,
                                padding: "6px 9px",
                              }}
                            >
                              Remove
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f9fbfd" }}>
                    <td
                      colSpan={4}
                      style={{
                        ...tableCellStyle,
                        fontSize: "12px",
                        color: REPORT_COLORS.inkSoft,
                      }}
                    >
                      Total monthly commitment kept ·{" "}
                      {
                        live.activeCommitments.filter(
                          (item) => item.likely_lender,
                        ).length
                      }{" "}
                      likely lender(s) detected
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        textAlign: "right",
                        fontSize: "20px",
                        fontWeight: 800,
                      }}
                    >
                      {formatMoney(
                        live.monthlyCommitment,
                        overview.primary_currency || "SGD",
                        0,
                      )}
                    </td>
                    <td colSpan={4} style={tableCellStyle} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={summaryRowStyle}>
              <div>
                <div style={labelStyle}>Daily balance heatmap</div>
                <div
                  style={{
                    fontSize: isMobile ? "22px" : "24px",
                    fontWeight: 800,
                    marginTop: "6px",
                  }}
                >
                  Where the account ran thin
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: isMobile ? "32px" : "48px",
                    lineHeight: 0.95,
                    fontWeight: 800,
                    color: REPORT_COLORS.amber,
                    letterSpacing: "-0.04em",
                  }}
                >
                  {Number(heatmap.buffer_days || 0).toFixed(1)}
                  <span
                    style={{
                      fontSize: isMobile ? "18px" : "24px",
                      marginLeft: "2px",
                    }}
                  >
                    d
                  </span>
                </div>
                <div style={{ ...labelStyle, marginTop: "6px" }}>
                  Buffer days
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: "26px",
                display: "flex",
                width: "100%",
                maxWidth: "100%",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: `${heatColumnGap}px`,
                overflow: "hidden",
                boxSizing: "border-box",
                paddingBottom: "6px",
              }}
            >
              {(heatmap.months || []).map((month) => {
                const { min: monthHeatMin, max: monthHeatMax } =
                  getMonthHeatBounds(month);

                return (
                  <div
                    key={month.month_key}
                    style={{
                      width: `${heatColumnWidth}px`,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flex: "0 1 auto",
                    }}
                  >
                    <div
                      style={{
                        fontSize: `${heatMonthLabelSize}px`,
                        fontWeight: 700,
                        color: "#37527d",
                        textAlign: "center",
                        marginBottom: `${Math.max(6, Math.min(12, heatCellSize - 2))}px`,
                        lineHeight: 1.1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatShortMonth(month.month_label)}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: `${heatDayGap}px`,
                        alignItems: "center",
                      }}
                    >
                      {normalizeHeatmapColumnDays(month.days, 30).map((day) => {
                        return (
                          <div
                            key={day.id}
                            title={
                              day.date
                                ? `${formatDateLabel(day.date)} · ${formatMoney(day.balance, overview.primary_currency || "SGD", 2)}`
                                : "No balance data"
                            }
	                            style={{
	                              width: `${heatCellSize}px`,
	                              height: `${heatCellSize}px`,
	                              borderRadius: `${Math.max(3, Math.min(7, Math.floor(heatCellSize / 2)))}px`,
                              background:
                                day.balance === null
                                  ? "#eef2f7"
                                  : getHeatmapColor(
                                      Number(day.balance),
                                      monthHeatMin,
                                      monthHeatMax,
                                    ),
                              boxSizing: "border-box",
	                              border: day.isLow
	                                ? `${heatCellSize <= 8 ? 2 : 3}px solid #19355f`
	                                : "1px solid rgba(255,255,255,0.6)",
                              boxShadow: day.isLow
                                ? "0 1px 0 rgba(25,53,95,0.08)"
                                : "none",
                            }}
                          />
                        );
                      })}
                    </div>
                    <div
	                      style={{
	                        fontSize: `${heatLowLabelSize}px`,
	                        color: "#37527d",
	                        textAlign: "center",
	                        marginTop: `${Math.max(6, Math.min(10, heatCellSize - 3))}px`,
	                        whiteSpace: "nowrap",
	                        maxWidth: `${Math.max(38, heatCellSize * 4)}px`,
	                        overflow: "hidden",
	                        textOverflow: "ellipsis",
	                      }}
                    >
                      low {formatCompactMoney(month.low_balance)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                ...summaryRowStyle,
                marginTop: "28px",
                fontSize: isMobile ? "12px" : "16px",
                color: "#37527d",
                gap: "14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexWrap: "wrap",
                }}
              >
                <span>Lower</span>
                {[
                  REPORT_COLORS.red,
                  "#ea8a4d",
                  "#f6c33e",
                  "#9ed57d",
                  REPORT_COLORS.green,
                ].map((color) => (
                  <span
                    key={color}
                    style={{
                      width: isMobile ? "14px" : "22px",
                      height: isMobile ? "14px" : "22px",
                      borderRadius: isMobile ? "6px" : "8px",
                      background: color,
                    }}
                  />
                ))}
                <span>Higher</span>
              </div>
              <div>
                Lowest balance covers ~
                {Number(heatmap.buffer_days || 0).toFixed(0)} days of avg daily
                burn
              </div>
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <div style={summaryRowStyle}>
            <div>
              <div style={labelStyle}>Lump-sum deposits detected</div>
              <div
                style={{ fontSize: "24px", fontWeight: 800, marginTop: "6px" }}
              >
                Decide what counts as revenue
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: REPORT_COLORS.inkSoft,
                  marginTop: "6px",
                  lineHeight: 1.6,
                }}
              >
                Large or one-off credits are flagged for review. Untick items
                that are not true operating income and the DSR denominator
                updates live.
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={labelStyle}>Adjusted revenue</div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 800,
                  color: REPORT_COLORS.green,
                }}
              >
                {formatMoney(
                  live.adjustedRevenueTotal,
                  overview.primary_currency || "SGD",
                  0,
                )}
              </div>
              <div style={{ fontSize: "11px", color: REPORT_COLORS.inkSoft }}>
                {overview.months_covered_count || 0}-month basis
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "14px",
            }}
          >
            <button
              type="button"
              onClick={() => setPickerMode("revenue")}
              style={secondaryButtonStyle}
            >
              Add credit transaction
            </button>
            <button
              type="button"
              onClick={resetRevenueSelection}
              style={resetButtonStyle}
            >
              Reset
            </button>
          </div>
          <div
            style={{
              ...manualEntryGridStyle,
              gridTemplateColumns: isMobile
                ? "1fr"
                : manualEntryGridStyle.gridTemplateColumns,
            }}
          >
            <input
              type="text"
              value={manualRevenueForm.title}
              onChange={(event) =>
                setManualRevenueForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="Manual revenue title"
              style={filterInputStyle}
            />
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={manualRevenueForm.amount}
              onChange={(event) =>
                setManualRevenueForm((current) => ({
                  ...current,
                  amount: event.target.value,
                }))
              }
              placeholder="Revenue amount"
              style={filterInputStyle}
            />
            <button
              type="button"
              onClick={addTypedManualLumpSum}
              style={actionButtonStyle}
            >
              Add manual revenue
            </button>
          </div>

          <div style={{ overflowX: "auto", marginTop: "14px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tableHeaderCellStyle}>Include?</th>
                  <th style={tableHeaderCellStyle}>Date</th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Amount
                  </th>
                  <th style={tableHeaderCellStyle}>Description</th>
                  <th style={tableHeaderCellStyle}>Classification</th>
                  <th style={tableHeaderCellStyle}>Reason</th>
                  <th style={tableHeaderCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(live.lumpSums || []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        ...tableCellStyle,
                        color: REPORT_COLORS.inkSoft,
                      }}
                    >
                      No lump-sum credits were detected in the current group.
                    </td>
                  </tr>
                ) : (
                  live.lumpSums.map((item) => (
                    <tr
                      key={item.id}
                      style={{ background: item.included ? "#fff" : "#fafcff" }}
                    >
                      <td style={tableCellStyle}>
                        <input
                          type="checkbox"
                          checked={item.included}
                          onChange={() => updateLumpSum(item.id)}
                          style={{
                            width: "16px",
                            height: "16px",
                            accentColor: REPORT_COLORS.blue,
                          }}
                        />
                      </td>
                      <td style={tableCellStyle}>
                        {formatDateLabel(item.date)}
                      </td>
                      <td
                        style={{
                          ...tableCellStyle,
                          textAlign: "right",
                          fontWeight: 700,
                        }}
                      >
                        {formatMoney(
                          item.amount,
                          item.currency || overview.primary_currency || "SGD",
                          0,
                        )}
                      </td>
                      <td style={tableCellStyle}>{item.description || "-"}</td>
                      <td style={tableCellStyle}>{item.classification}</td>
                      <td
                        style={{
                          ...tableCellStyle,
                          color: REPORT_COLORS.inkSoft,
                        }}
                      >
                        {item.reason}
                      </td>
                      <td style={tableCellStyle}>
                        {item.manual ? (
                          <button
                            type="button"
                            onClick={() => removeManualLumpSum(item.id)}
                            style={{
                              ...secondaryButtonStyle,
                              padding: "6px 9px",
                            }}
                          >
                            Remove
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f9fbfd" }}>
                  <td
                    colSpan={2}
                    style={{
                      ...tableCellStyle,
                      fontSize: "12px",
                      color: REPORT_COLORS.inkSoft,
                    }}
                  >
                    Excluded lump sums:{" "}
                    <strong style={{ color: REPORT_COLORS.ink }}>
                      {formatMoney(
                        live.excludedRevenueTotal,
                        overview.primary_currency || "SGD",
                        0,
                      )}
                    </strong>{" "}
                    · {live.excludedLumpSums.length} item(s)
                  </td>
                  <td
                    style={{
                      ...tableCellStyle,
                      textAlign: "right",
                      fontSize: "20px",
                      fontWeight: 800,
                    }}
                  >
                    {formatMoney(
                      live.adjustedRevenueTotal,
                      overview.primary_currency || "SGD",
                      0,
                    )}
                  </td>
                  <td
                    colSpan={4}
                    style={{
                      ...tableCellStyle,
                      fontSize: "12px",
                      color: REPORT_COLORS.inkSoft,
                    }}
                  >
                    The excluded amount is removed from the revenue basis used
                    for DSR.
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <section
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: "18px",
          }}
        >
          {[
            {
              title: "Credit strengths",
              items: drilldowns.strengths || [],
              band: "green",
            },
            {
              title: "Credit concerns",
              items: drilldowns.concerns || [],
              band: "amber",
            },
            {
              title: "Risk flags",
              items: drilldowns.risk_flags || [],
              band: "red",
              rich: true,
            },
          ].map((section) => {
            const colors = bandColors(section.band);
            return (
              <div
                key={section.title}
                style={{
                  ...cardStyle,
                  borderLeft: `4px solid ${colors.text}`,
                }}
              >
                <div style={{ ...labelStyle, color: colors.text }}>
                  {section.title}
                </div>
                <div
                  style={{ display: "grid", gap: "12px", marginTop: "14px" }}
                >
                  {section.rich
                    ? (section.items || []).map((item, index) => (
                        <div key={`${section.title}-${index}`}>
                          <div style={summaryRowStyle}>
                            <div style={{ fontWeight: 700 }}>{item.title}</div>
                            <div
                              style={pillStyle(
                                item.severity === "high" ? "red" : "amber",
                              )}
                            >
                              {item.severity || "Medium"}
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: "5px",
                              fontSize: "12px",
                              color: REPORT_COLORS.inkSoft,
                            }}
                          >
                            {item.detail}
                          </div>
                        </div>
                      ))
                    : (section.items || []).map((item, index) => (
                        <div
                          key={`${section.title}-${index}`}
                          style={{
                            display: "flex",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <span style={{ color: colors.text }}>●</span>
                          <span>{item}</span>
                        </div>
                      ))}
                </div>
              </div>
            );
          })}
        </section>

        <div style={labelStyle}>Evidence & drilldowns</div>

        <Drilldown
          title="Cash flow waterfall — by month"
          subtitle="Opening → +Deposits → −Withdrawals → Closing"
        >
          <div
            style={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              paddingBottom: "6px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.max(waterfallItems.length, 1)}, minmax(118px, 1fr))`,
                gap: "12px",
                minWidth: waterfallItems.length > 4
                  ? `${waterfallItems.length * 130}px`
                  : "100%",
              }}
            >
              {waterfallItems.map((item) => {
                const barHeight = (value) =>
                  `${Math.max(4, (Number(value || 0) / waterfallGlobalMax) * 118)}px`;

                return (
                  <div
                    key={item.month_key}
                    style={{
                      borderRadius: "16px",
                      border: `1px solid ${REPORT_COLORS.line}`,
                      padding: "12px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        textAlign: "center",
                        marginBottom: "10px",
                      }}
                    >
                      {item.month_label}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "stretch",
                        justifyContent: "center",
                        gap: isMobile ? "8px" : "10px",
                        height: "154px",
                      }}
                    >
                      {[
                        {
                          label: "Open",
                          color: "#c7d0dc",
                          value: item.opening_balance,
                        },
                        {
                          label: "In",
                          color: REPORT_COLORS.green,
                          value: item.deposits,
                        },
                        {
                          label: "Out",
                          color: REPORT_COLORS.red,
                          value: item.withdrawals,
                        },
                        {
                          label: "Close",
                          color: REPORT_COLORS.blue,
                          value: item.closing_balance,
                        },
                      ].map((bar) => (
                        <div
                          key={bar.label}
                          style={{
                            width: `${waterfallBarWidth}px`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            title={`${bar.label}: ${formatMoney(bar.value, overview.primary_currency || "SGD", 2)}`}
                            style={{
                              width: `${waterfallBarWidth}px`,
                              height: barHeight(bar.value),
                              borderRadius: "8px",
                              background: bar.color,
                            }}
                          />
                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "10px",
                              color: REPORT_COLORS.inkSoft,
                            }}
                          >
                            {bar.label}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        marginTop: "10px",
                        textAlign: "center",
                        fontSize: "11px",
                        color:
                          Number(item.net_cash_flow) >= 0
                            ? REPORT_COLORS.green
                            : REPORT_COLORS.red,
                        fontWeight: 700,
                      }}
                    >
                      Net {Number(item.net_cash_flow) >= 0 ? "+" : ""}
                      {formatMoney(
                        item.net_cash_flow,
                        overview.primary_currency || "SGD",
                        0,
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Drilldown>

        <section
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: "18px",
          }}
        >
          <Drilldown
            title="Inflow concentration"
            subtitle="Top company payers as % of deposits"
          >
            <div
              style={{
                display: "flex",
                gap: "18px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <DonutChart segments={live.concentrationSegments} />
              <div
                style={{
                  flex: 1,
                  minWidth: "220px",
                  display: "grid",
                  gap: "10px",
                }}
              >
                {live.concentrationSegments.map((item) => (
                  <div key={item.label} style={summaryRowStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "3px",
                          background: item.color,
                        }}
                      />
                      <span style={{ fontSize: "13px" }}>{item.label}</span>
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 700 }}>
                      {formatPercent(item.sharePercent)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Drilldown>

          <Drilldown
            title="True operating income"
            subtitle="Recurring vs one-off, auto-separated"
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px",
              }}
            >
              <div
                style={{
                  borderRadius: "16px",
                  padding: "16px",
                  background: REPORT_COLORS.greenSoft,
                }}
              >
                <div style={{ ...labelStyle, color: REPORT_COLORS.green }}>
                  Recurring inflow
                </div>
                <div
                  style={{
                    fontSize: "26px",
                    fontWeight: 800,
                    marginTop: "4px",
                  }}
                >
                  {formatMoney(
                    recurringMonthlyInflow,
                    overview.primary_currency || "SGD",
                    0,
                  )}
                </div>
                <div style={{ fontSize: "11px", color: REPORT_COLORS.inkSoft }}>
                  avg / mo
                </div>
              </div>
              <div
                style={{
                  borderRadius: "16px",
                  padding: "16px",
                  background: REPORT_COLORS.blueSoft,
                }}
              >
                <div style={{ ...labelStyle, color: REPORT_COLORS.blue }}>
                  One-off / review
                </div>
                <div
                  style={{
                    fontSize: "26px",
                    fontWeight: 800,
                    marginTop: "4px",
                  }}
                >
                  {formatMoney(
                    oneOffMonthlyInflow,
                    overview.primary_currency || "SGD",
                    0,
                  )}
                </div>
                <div style={{ fontSize: "11px", color: REPORT_COLORS.inkSoft }}>
                  avg / mo
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: "14px",
                fontSize: "12px",
                color: REPORT_COLORS.inkSoft,
                lineHeight: 1.6,
              }}
            >
              Recommended DSR denominator:{" "}
              <strong style={{ color: REPORT_COLORS.ink }}>
                {drilldowns.operating_income?.recommended_basis ||
                  "Adjusted revenue"}
              </strong>{" "}
              using the current inclusion choices shown above.
            </div>
          </Drilldown>
        </section>

        <Drilldown
          title="Existing lender exposure"
          subtitle="Rolled up from kept commitments"
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tableHeaderCellStyle}>Lender / counterparty</th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Monthly outflow
                  </th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Months seen
                  </th>
                  <th style={tableHeaderCellStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {liveLenderExposure.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        ...tableCellStyle,
                        color: REPORT_COLORS.inkSoft,
                      }}
                    >
                      No lender-style commitments are currently kept in the live
                      scenario.
                    </td>
                  </tr>
                ) : (
                  liveLenderExposure.map((item, index) => (
                    <tr key={`${item.counterparty}-${index}`}>
                      <td style={{ ...tableCellStyle, fontWeight: 700 }}>
                        {item.counterparty}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        {formatMoney(
                          item.monthly_outflow,
                          overview.primary_currency || "SGD",
                          0,
                        )}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        {item.months_seen}
                      </td>
                      <td
                        style={{
                          ...tableCellStyle,
                          color: REPORT_COLORS.inkSoft,
                        }}
                      >
                        {item.notes}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Drilldown>

        <Drilldown
          title="Anomaly feed"
          subtitle="Auto-flagged transactions for review"
        >
          <div style={{ display: "grid", gap: "12px" }}>
            {(drilldowns.anomalies || []).length === 0 ? (
              <div style={{ fontSize: "13px", color: REPORT_COLORS.inkSoft }}>
                No anomalies are currently flagged.
              </div>
            ) : (
              (drilldowns.anomalies || []).map((item, index) => (
                <div
                  key={`${item.type}-${index}`}
                  style={{
                    display: "flex",
                    gap: "14px",
                    alignItems: "flex-start",
                    padding: "14px",
                    borderRadius: "14px",
                    background: "#f9fbfd",
                  }}
                >
                  <div
                    style={pillStyle(
                      item.type === "Data quality" ||
                        item.type === "Negative balance"
                        ? "red"
                        : item.type === "Lump credit"
                          ? "blue"
                          : "amber",
                    )}
                  >
                    {item.type}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {item.amount !== null && item.amount !== undefined
                        ? `${formatMoney(item.amount, item.currency || overview.primary_currency || "SGD", 2)} · `
                        : ""}
                      {item.description || item.date || "Review item"}
                    </div>
                    <div
                      style={{
                        marginTop: "4px",
                        fontSize: "12px",
                        color: REPORT_COLORS.inkSoft,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.detail}
                    </div>
                  </div>
                  <div
                    style={{ fontSize: "12px", color: REPORT_COLORS.inkSoft }}
                  >
                    {item.date ? formatDateLabel(item.date) : "-"}
                  </div>
                </div>
              ))
            )}
          </div>
        </Drilldown>

        <Drilldown
          title={`Month-by-month detail · ${overview.primary_currency || "SGD"}`}
          subtitle="Source data"
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tableHeaderCellStyle}>Month</th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Deposits
                  </th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Withdrawals
                  </th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Net
                  </th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    End balance
                  </th>
                  <th style={{ ...tableHeaderCellStyle, textAlign: "right" }}>
                    Lowest day
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthlyDetailRows.map((item) => (
                  <tr key={item.month_key}>
                    <td style={{ ...tableCellStyle, fontWeight: 700 }}>
                      {item.month_label}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>
                      {formatMoney(
                        item.deposits,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>
                      {formatMoney(
                        item.withdrawals,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        textAlign: "right",
                        color:
                          Number(item.net_cash_flow) >= 0
                            ? REPORT_COLORS.green
                            : REPORT_COLORS.red,
                        fontWeight: 700,
                      }}
                    >
                      {Number(item.net_cash_flow) >= 0 ? "+" : ""}
                      {formatMoney(
                        item.net_cash_flow,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>
                      {formatMoney(
                        item.end_balance,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>
                      {formatMoney(
                        item.lowest_day_balance,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                      <div
                        style={{
                          fontSize: "10px",
                          color: REPORT_COLORS.inkSoft,
                        }}
                      >
                        {formatDateLabel(item.lowest_day_date)}
                      </div>
                    </td>
                  </tr>
                ))}
                {monthlyDetailRows.length > 0 ? (
                  <tr style={{ background: "#f9fbfd" }}>
                    <td
                      style={{
                        ...tableCellStyle,
                        borderTop: `2px solid ${REPORT_COLORS.line}`,
                        fontWeight: 800,
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        borderTop: `2px solid ${REPORT_COLORS.line}`,
                        textAlign: "right",
                        fontWeight: 800,
                      }}
                    >
                      {formatMoney(
                        monthlyDetailTotal.deposits,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        borderTop: `2px solid ${REPORT_COLORS.line}`,
                        textAlign: "right",
                        fontWeight: 800,
                      }}
                    >
                      {formatMoney(
                        monthlyDetailTotal.withdrawals,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        borderTop: `2px solid ${REPORT_COLORS.line}`,
                        textAlign: "right",
                        color:
                          monthlyDetailTotal.net_cash_flow === null
                            ? REPORT_COLORS.ink
                            : Number(monthlyDetailTotal.net_cash_flow) >= 0
                            ? REPORT_COLORS.green
                            : REPORT_COLORS.red,
                        fontWeight: 800,
                      }}
                    >
                      {monthlyDetailTotal.net_cash_flow !== null &&
                      Number(monthlyDetailTotal.net_cash_flow) >= 0
                        ? "+"
                        : ""}
                      {formatMoney(
                        monthlyDetailTotal.net_cash_flow,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        borderTop: `2px solid ${REPORT_COLORS.line}`,
                        textAlign: "right",
                        fontWeight: 800,
                      }}
                    >
                      {formatMoney(
                        monthlyDetailTotal.end_balance,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        borderTop: `2px solid ${REPORT_COLORS.line}`,
                        textAlign: "right",
                        fontWeight: 800,
                      }}
                    >
                      {formatMoney(
                        monthlyDetailTotal.lowest_day_balance,
                        overview.primary_currency || "SGD",
                        2,
                      )}
                      <div
                        style={{
                          fontSize: "10px",
                          color: REPORT_COLORS.inkSoft,
                          fontWeight: 600,
                        }}
                      >
                        {formatDateLabel(monthlyDetailTotal.lowest_day_date)}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Drilldown>
      </div>
      {pickerMode === "dsr" ? (
        <TransactionPicker
          mode="debit"
          title="Add transaction to DSR"
          transactions={pickerTransactions}
          selectedIds={selectedManualCommitmentIds}
          currency={overview.primary_currency || "SGD"}
          onAdd={addManualCommitment}
          onClose={() => setPickerMode(null)}
          isMobile={isMobile}
        />
      ) : null}
      {pickerMode === "revenue" ? (
        <TransactionPicker
          mode="credit"
          title="Add transaction to revenue"
          transactions={pickerTransactions}
          selectedIds={selectedManualLumpSumIds}
          currency={overview.primary_currency || "SGD"}
          onAdd={addManualLumpSum}
          onClose={() => setPickerMode(null)}
          isMobile={isMobile}
        />
      ) : null}
      <FraudBboxPreviewModal
        preview={fraudPreview}
        onClose={() => setFraudPreview(null)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default BankStatementGroupReport;
